import { useAppDispatch, useAppSelector } from "@src/store/hooks";
import styles from "./DeleteListing.module.scss";
import React, { useState } from "react";
import XIconThemed from "@src/assets/general-icons/XIconThemed";
import { Button, ButtonVariant } from "../button/Button";
import { DateFormatsEnum, formatDate } from "../utils/dateUtils";
import Checkbox from "../vGrid/Checkbox";
import { deleteListingThunk } from "@src/store/pricing/deleteListing";
import { toast } from "sonner";
import { eventActions } from "@src/store";

type TDeleteListing = {
	handleClose?: () => void;
	fetchInventory?: (skipCache?: boolean) => void;
};

const DeleteListing = ({ handleClose, fetchInventory }: TDeleteListing) => {
	const inventoryData = useAppSelector((state) => state.pricing.eventInventoryAll.data);
	const event = useAppSelector((state) => state.pricing.selection.event);
	const [ticketId] = useAppSelector((state) => state.pricing.selection.inventoryItemIds);
	const selectedListingId = inventoryData.find((d) => d.Item_ID === ticketId)?.Listing_ID;
	const listingItems = inventoryData.filter((i) => i.Listing_ID === selectedListingId);
	const CompanyName = useAppSelector((state) => state.settings.userCompanyName);
	const isLoading = useAppSelector((state) => state.pricing.deleteListing.loading);
	listingItems.sort((a, b) => a.Seat_Number - b.Seat_Number);

	const dispatch = useAppDispatch();

	const [ItemIds, setItemIds] = useState<Array<number>>([]);
	const [DeleteReason, setDeleteReason] = useState("");

	const handleCheckboxChange = (itemId: number) => {
		const isSelected = ItemIds.includes(itemId);
		if (isSelected) setItemIds((prev) => prev.filter((y) => y !== itemId));
		else setItemIds((prev) => [...prev, itemId]);
	};

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!CompanyName) {
			toast.error("Company name unavailable");
			return;
		}

		const response = await dispatch(
			deleteListingThunk({
				CompanyName,
				DeleteReason,
				ItemIds,
			}),
		);

		if (response.meta.requestStatus === "fulfilled") {
			fetchInventory?.(true);
			handleClose?.();
			dispatch(eventActions.setInventory({ inventoryItemIds: [] }));
		}
	};

	return (
		<form onSubmit={handleSubmit} className={styles.root}>
			<div className={styles.header}>
				<div>Delete Listing</div>
				<div data-no-drag aria-hidden="true" style={{ cursor: "pointer" }} onClick={handleClose}>
					<XIconThemed color="#fafafa" />
				</div>
			</div>
			<div className={styles.content}>
				<div className={styles.details}>
					<div className={styles.top}>
						<div className={styles.textPrimary}>Event Information</div>
						<div className={styles.topSection}>
							<div className={styles.labelValue}>
								<div className={styles.textSecondary}>Name</div>
								<div className={styles.textPrimary}>{event?.Event_Headliner}</div>
							</div>
							<div className={styles.innerFlex}>
								<div style={{ minWidth: 100 }} className={styles.labelValue}>
									<div className={styles.textSecondary}>Date</div>
									<div className={styles.textPrimary}>
										{formatDate(event?.Event_Date, DateFormatsEnum["MM/DD/YYYY"])}
									</div>
								</div>
								<div style={{ minWidth: 100 }} className={styles.labelValue}>
									<div className={styles.textSecondary}>Time</div>
									<div className={styles.textPrimary}>{event?.Event_Time}</div>
								</div>
							</div>

							<div className={styles.innerFlex}>
								<div style={{ minWidth: 100 }} className={styles.labelValue}>
									<div className={styles.textSecondary}>Section</div>
									<div className={styles.textPrimary}>{listingItems?.[0]?.Section}</div>
								</div>
								<div style={{ minWidth: 100 }} className={styles.labelValue}>
									<div className={styles.textSecondary}>Row</div>
									<div className={styles.textPrimary}>{listingItems?.[0]?.Row}</div>
								</div>
							</div>

							<div className={styles.smallFlex}>
								<Checkbox
									isChecked={ItemIds.length === listingItems.length}
									onChange={() => {
										if (ItemIds.length === listingItems.length) setItemIds([]);
										else setItemIds(listingItems.map((x) => x.Item_ID));
									}}
								/>
								<div className={styles.textPrimary}>Select all</div>
							</div>

							<div className={styles.innerFlex} style={{ gap: "1.6rem" }}>
								{listingItems.map((x) => {
									const isSelected = ItemIds.includes(x.Item_ID);
									return (
										<div key={x.Item_ID} className={styles.smallFlex}>
											<Checkbox isChecked={isSelected} onChange={() => handleCheckboxChange(x.Item_ID)} />
											<div className={styles.textPrimary}>{x.Seat_Number}</div>
										</div>
									);
								})}
							</div>
						</div>
					</div>
					<div className={styles.bottom}>
						<div className={styles.textPrimary}>Reason for Deletion:</div>
						<textarea
							value={DeleteReason}
							onChange={(e) => setDeleteReason(e.target.value)}
							placeholder="Please provide a detailed explanation for the deletion of this listing, including any relevant reasons or circumstances."
						/>
					</div>
				</div>
				<div className={styles.buttons}>
					<Button
						disabled={isLoading}
						style={{ padding: "7.5px 16px", width: 80 }}
						onClick={handleClose}
						variant={ButtonVariant.Secondary}>
						Cancel
					</Button>
					<Button
						disabled={!ItemIds.length || !DeleteReason || isLoading}
						type="submit"
						style={{ padding: "7.5px 16px", width: 80 }}
						variant={ButtonVariant.Primary}>
						Apply
					</Button>
				</div>
			</div>
		</form>
	);
};

export default DeleteListing;
